import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Briefcase, Plus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, projectStatusVariant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatDate } from '../lib/utils'

type Project = {
  id: string
  name: string
  code: string | null
  description: string | null
  status: string
  kind: string
  startDate: string | null
  endDate: string | null
  updatedAt: string
  clientId: string | null
}

type ListResult = { results: Project[]; total: number }
type Client = { id: string; name: string }

export function ProjectsPage() {
  const qc = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.get<ListResult>('/projects'),
  })

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { all: true }],
    queryFn: () => apiClient.get<{ results: Client[] }>('/clients?limit=200'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/projects', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      setDialogOpen(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body = Object.fromEntries(fd.entries())
    if (!body.clientId) Reflect.deleteProperty(body, 'clientId')
    create.mutate(body)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Progetti"
        description={`${data?.total ?? 0} progetti totali`}
        action={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> Nuovo progetto
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Nome</th>
              <th className="text-left px-5 py-3 font-medium">Codice</th>
              <th className="text-left px-5 py-3 font-medium">Tipo</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-left px-5 py-3 font-medium">Periodo</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-5 py-3" colSpan={5}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : data && data.results.length > 0 ? (
              data.results.map((p) => (
                <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/40 transition">
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-100">{p.name}</div>
                    {p.description && (
                      <div className="text-xs text-zinc-500 line-clamp-1">{p.description}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-zinc-400 tabular-nums">{p.code ?? '—'}</td>
                  <td className="px-5 py-3 text-zinc-300 capitalize">
                    {p.kind.replace(/_/g, ' ')}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={projectStatusVariant(p.status)}>
                      {p.status.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">
                    {p.startDate ? formatDate(p.startDate) : '—'} →{' '}
                    {p.endDate ? formatDate(p.endDate) : '—'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <Empty
                    icon={Briefcase}
                    title="Nessun progetto"
                    description="Crea un progetto per iniziare a tracciare feature, task e tempi."
                    action={
                      <Button onClick={() => setDialogOpen(true)}>
                        <Plus className="size-4" /> Nuovo progetto
                      </Button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuovo progetto"
        description="Solo Nome è obbligatorio. Tutto il resto opzionale."
        size="lg"
      >
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" name="name" required placeholder="Sito web ACME 2026" />
            </div>
            <div>
              <Label htmlFor="code">Codice</Label>
              <Input id="code" name="code" placeholder="ACME-WEB-2026" />
            </div>
          </div>
          <div>
            <Label htmlFor="description">Descrizione</Label>
            <Textarea id="description" name="description" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="clientId">Cliente</Label>
              <Select id="clientId" name="clientId" defaultValue="">
                <option value="">— Nessuno —</option>
                {clientsData?.results.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="kind">Tipo</Label>
              <Select id="kind" name="kind" defaultValue="time_and_materials">
                <option value="fixed_price">Fixed price</option>
                <option value="time_and_materials">T&M</option>
                <option value="retainer">Retainer</option>
                <option value="internal">Interno</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Stato</Label>
              <Select id="status" name="status" defaultValue="active">
                <option value="lead">Lead</option>
                <option value="proposal">Proposal</option>
                <option value="active">Active</option>
                <option value="on_hold">On hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="startDate">Inizio</Label>
              <Input id="startDate" name="startDate" type="date" />
            </div>
            <div>
              <Label htmlFor="endDate">Fine</Label>
              <Input id="endDate" name="endDate" type="date" />
            </div>
          </div>
          {create.isError && (
            <div className="text-sm text-red-400">
              {(create.error as Error)?.message ?? 'Errore'}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creazione…' : 'Crea progetto'}
            </Button>
          </div>
        </form>
      </Dialog>
    </AppLayout>
  )
}
